import React from "react";
import { Plus, Trash2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { useT } from "@/lib/LanguageContext";
import { ServiceCheckboxGroup } from "./ServiceCheckboxGroup";

// One row = one weekday of the template. `windows` is the backend array
// [{start: "09:00", end: "12:00", service_ids?: [...]}, ...]; a closed day is an empty array.
//
// Each window may name its OWN set of catalogue services (`service_ids`), overriding the
// template-level default. `services` is the practice's ACTIVE catalogue; an empty
// catalogue renders no picker at all (there is nothing to choose between). `error` is the
// one sentence explaining what is wrong with this day, whoever found it — the editor's
// own checks or the server's `WINDOW_WITHOUT_SERVICE` refusal naming this day.
export const DayWindowsRow = ({ dayKey, label, windows, onChange, error, services = [] }) => {
  const t = useT("admin");
  const enabled = windows.length > 0;

  const toggle = (checked) => {
    onChange(checked ? [{ start: "09:00", end: "17:00" }] : []);
  };
  const updateWindow = (idx, field, value) => {
    onChange(windows.map((w, i) => (i === idx ? { ...w, [field]: value } : w)));
  };
  // INHERIT IS THE ABSENCE OF THE KEY, not an empty array: the backend reads a missing
  // `service_ids` as "use the template default" and never persists an explicit `[]`
  // (template_service._resolve_weekly_services) — the two spellings of "nothing chosen
  // here" must stay one fact.
  const setWindowServices = (idx, ids) => {
    onChange(windows.map((w, i) => {
      if (i !== idx) return w;
      const { service_ids: _sids, ...rest } = w;
      return ids.length ? { ...rest, service_ids: ids } : rest;
    }));
  };
  const addWindow = () => onChange([...windows, { start: "09:00", end: "17:00" }]);
  const removeWindow = (idx) => onChange(windows.filter((_, i) => i !== idx));

  return (
    <div
      className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4 py-3 border-b border-eh-border-soft/60 last:border-0"
      data-testid={`day-row-${label}`}
    >
      <div className="flex items-center gap-2 sm:w-32 shrink-0 pt-1">
        <Switch checked={enabled} onCheckedChange={toggle} data-testid={`day-toggle-${label}`} />
        <span className="text-sm font-medium text-eh-ink">{label}</span>
      </div>
      <div className="flex-1 min-w-0">
        {!enabled && <span className="text-xs text-eh-muted">{t("sched.dayClosed")}</span>}
        {enabled && (
          <div className="space-y-1.5">
            {windows.map((w, idx) => (
              <div key={idx} className="space-y-1" data-testid={`window-${label}-${idx}`}>
                <div className="flex items-center gap-2">
                  <Input
                    type="time"
                    value={w.start}
                    onChange={(e) => updateWindow(idx, "start", e.target.value)}
                    className="h-8 w-28"
                    data-testid={`window-start-${label}-${idx}`}
                  />
                  <span className="text-eh-muted text-xs">–</span>
                  <Input
                    type="time"
                    value={w.end}
                    onChange={(e) => updateWindow(idx, "end", e.target.value)}
                    className="h-8 w-28"
                    data-testid={`window-end-${label}-${idx}`}
                  />
                  <button
                    type="button"
                    onClick={() => removeWindow(idx)}
                    className="p-1.5 rounded-full hover:bg-red-50 text-red-600"
                    data-testid={`window-remove-${label}-${idx}`}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
                {services.length > 0 && (
                  <div className="pl-1 flex flex-wrap gap-x-3 gap-y-1">
                    <ServiceCheckboxGroup
                      services={services}
                      selectedIds={w.service_ids || []}
                      onChange={(ids) => setWindowServices(idx, ids)}
                      idPrefix={`window-service-${label}-${idx}`}
                    />
                  </div>
                )}
                {services.length > 0 && !(w.service_ids || []).length && (
                  <p className="text-[11px] text-eh-muted pl-1">{t("sched.windowServicesHint")}</p>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={addWindow}
              className="inline-flex items-center gap-1 text-xs text-eh-sage-deep hover:underline"
              data-testid={`window-add-${label}`}
            >
              <Plus size={12} /> {t("sched.addWindow")}
            </button>
          </div>
        )}
        {error && (
          <div className="text-xs text-red-600 mt-1" role="alert" data-testid={`day-error-${dayKey}`}>{error}</div>
        )}
      </div>
    </div>
  );
};
